import getFetcher from 'api/utils/getFetcher';
import { useCallback, useMemo } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import {
  EnumsSimNetworkState,
  EnumsSimStatus,
  HandlersSimStatusesResponse,
} from 'types/__generated__/SimInventoryApi';
import { ApiBaseError } from '../utils/shared';

export interface SimStatusesResultError extends ApiBaseError {}

type SimStatusesSWRConfiguration = SWRConfiguration<
  HandlersSimStatusesResponse,
  SimStatusesResultError
>;

const useSimStatuses = () => {
  const url = `${process.env.VITE_BIZ_API_ROOT}/sim-inventory/sims/simStatuses`;

  const config: SimStatusesSWRConfiguration = useSWRConfig();

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    HandlersSimStatusesResponse,
    SimStatusesResultError
  >(
    url,
    getFetcher,
    // This data should only be refreshed once per day
    { ...config, dedupingInterval: 86400000, focusThrottleInterval: 86400000 }
  );

  const { friendlyStatusLookup, internalStatusLookup } = useMemo(() => {
    const formatLookup = new Map<EnumsSimNetworkState | 'PENDING', EnumsSimStatus>();

    // This probably doesn't need to be a Map+Set, but format does since it's called A LOT
    const internalLookup = new Map<EnumsSimStatus, Set<EnumsSimNetworkState>>();

    data?.networkStatusMap?.forEach((value) => {
      if (value.friendlyNetworkStatus) {
        value.internalNetworkStatus?.forEach((internalValue) => {
          formatLookup.set(internalValue, value.friendlyNetworkStatus!);
        });

        internalLookup.set(value.friendlyNetworkStatus, new Set(value.internalNetworkStatus));
      }
    });

    // TODO: Remove this when we add it to the API
    formatLookup.set('PENDING', 'Pending');

    return { friendlyStatusLookup: formatLookup, internalStatusLookup: internalLookup };
  }, [data]);

  // Take what the API has told us are the possible and build fast lookups.
  const formatStatus = useCallback(
    (networkStatus: EnumsSimNetworkState | 'PENDING') =>
      networkStatus === 'PENDING'
        ? 'Pending'
        : (friendlyStatusLookup.get(networkStatus) ?? 'Pending'),
    [friendlyStatusLookup]
  );

  const getNetworkStatusFromFriendly = useCallback(
    (friendlyStatus: EnumsSimStatus) => [
      ...(internalStatusLookup.get(friendlyStatus) ?? new Set()).values(),
    ],
    [internalStatusLookup]
  );

  const networkStatusGroups = useMemo(() => data?.networkStatusGroups, [data]);

  return {
    networkStatusGroups,
    getNetworkStatusFromFriendly,
    formatStatus,
    isLoading,
    error,
    isValidating,
    mutate,
  };
};

export default useSimStatuses;
